'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  syncQueue,
  getQueueSize,
  getQueuedMutations,
  getLastSyncTime,
  SyncResult,
  QueuedMutation,
} from '@/lib/offline-queue'
import { isOfflineFirstEnabled } from '@/lib/feature-flags'
import { getOpenConflictCount } from '@/lib/offline'
import { dispatchSyncStatus } from '@/components/offline-indicator'

interface SyncState {
  isSyncing: boolean
  queueSize: number
  lastSyncResults: SyncResult[]
  pendingMutations: QueuedMutation[]
  lastSyncTime: number | null
  openConflictCount: number
  offlineFirstEnabled: boolean
}

interface UseSyncReturn extends SyncState {
  triggerSync: () => Promise<SyncResult[]>
  refreshQueueState: () => Promise<void>
}

/**
 * Hook to manage offline queue synchronization
 *
 * Features:
 * - Auto-syncs when coming back online (with exponential backoff on failure)
 * - Shows sync status via OfflineIndicator custom events
 * - Toast feedback on sync completion/failure
 * - Prevents concurrent sync runs
 * - Invalidates queries after successful sync
 * - Tracks last sync time for UI display
 */
export function useOfflineSync(): UseSyncReturn {
  const queryClient = useQueryClient()
  const [state, setState] = useState<SyncState>({
    isSyncing: false,
    queueSize: 0,
    lastSyncResults: [],
    pendingMutations: [],
    lastSyncTime: null,
    openConflictCount: 0,
    offlineFirstEnabled: isOfflineFirstEnabled(),
  })

  // Track retry attempts for exponential backoff on auto-sync
  const autoSyncAttemptRef = useRef(0)
  const autoSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Refresh queue state from IndexedDB
  const refreshQueueState = useCallback(async () => {
    try {
      const [size, mutations, lastSync, conflicts] = await Promise.all([
        getQueueSize(),
        getQueuedMutations(),
        getLastSyncTime(),
        getOpenConflictCount(),
      ])
      setState(prev => ({
        ...prev,
        queueSize: size,
        pendingMutations: mutations,
        lastSyncTime: lastSync,
        openConflictCount: conflicts,
        offlineFirstEnabled: isOfflineFirstEnabled(),
      }))
    } catch (e) {
      console.error('[offline-sync] Failed to refresh queue state from IndexedDB:', e)
      // Set safe defaults so the hook doesn't crash the app
      setState(prev => ({
        ...prev,
        queueSize: 0,
        pendingMutations: [],
        lastSyncTime: null,
        openConflictCount: 0,
      }))
    }
  }, [])

  // Use ref for sync guard to keep triggerSync identity stable
  const isSyncingRef = useRef(false)

  // Execute sync
  const triggerSync = useCallback(async (): Promise<SyncResult[]> => {
    if (!isOfflineFirstEnabled()) {
      return []
    }
    // Prevent concurrent syncs at the hook level (ref avoids dep on state.isSyncing)
    if (isSyncingRef.current) {
      return []
    }

    isSyncingRef.current = true
    setState(prev => ({ ...prev, isSyncing: true }))
    dispatchSyncStatus('syncing')

    try {
      const results = await syncQueue()

      // Empty results means another sync is already running (lock not acquired)
      if (results.length === 0) {
        setState(prev => ({ ...prev, isSyncing: false }))
        dispatchSyncStatus('idle')
        return results
      }

      const successCount = results.filter(r => r.success).length
      const failCount = results.filter(r => !r.success).length
      const hasSuccess = successCount > 0

      if (hasSuccess) {
        // Invalidate all order-related queries so UI refreshes
        queryClient.invalidateQueries({ queryKey: ['orders'] })
        queryClient.invalidateQueries({ queryKey: ['menu-items'] })

        // Toast with success count
        toast.success(
          `Synced ${successCount} order${successCount > 1 ? 's' : ''} successfully!`,
          { duration: 4000 }
        )

        // Reset auto-sync retry counter on success
        autoSyncAttemptRef.current = 0
      }

      if (failCount > 0) {
        dispatchSyncStatus('error')
        // Distinguish permanent failures (mutation removed from queue —
        // 409 conflict or max retries hit) from retriable ones. The
        // previous undifferentiated "will retry automatically" toast
        // misled staff when work was actually lost — they thought it
        // would come back on the next sync. Audit ref:
        // BACKEND_PRACTICES_2026-05-12 P1 #11.
        const terminalFailures = results.filter(r => !r.success && r.terminal)
        const retriableFailures = results.filter(r => !r.success && !r.terminal)

        if (terminalFailures.length > 0) {
          const sample = terminalFailures
            .map(r => r.error)
            .filter(Boolean)
            .slice(0, 1)
            .join('; ')
          toast.error(
            `${terminalFailures.length} order${terminalFailures.length > 1 ? 's' : ''} won't sync — please re-enter${sample ? ` (${sample})` : ''}.`,
            { duration: 10000 }
          )
        }
        if (retriableFailures.length > 0) {
          toast.warning(
            `${retriableFailures.length} order${retriableFailures.length > 1 ? 's' : ''} pending — will retry automatically.`,
            { duration: 5000 }
          )
        }
      } else {
        dispatchSyncStatus('idle')
      }

      const lastSync = await getLastSyncTime()

      isSyncingRef.current = false
      setState(prev => ({
        ...prev,
        isSyncing: false,
        lastSyncResults: results,
        lastSyncTime: lastSync,
      }))

      // Refresh queue state after sync
      await refreshQueueState()

      return results
    } catch (error) {
      console.error('Sync failed:', error)
      dispatchSyncStatus('error')
      isSyncingRef.current = false
      setState(prev => ({ ...prev, isSyncing: false }))
      return []
    }
  }, [queryClient, refreshQueueState])

  // Schedule a retry with exponential backoff
  // Declared before the auto-sync effect to satisfy TDZ/use-before-declare lint.
  // Uses a ref for recursive self-reference so the callback identity stays stable.
  const scheduleRetryRef = useRef<() => void>(() => {})
  const scheduleRetry = useCallback(() => {
    if (autoSyncTimeoutRef.current) {
      clearTimeout(autoSyncTimeoutRef.current)
    }

    autoSyncAttemptRef.current += 1
    // Backoff: 5s, 10s, 20s, 40s, capped at 60s
    const backoffDelay = Math.min(5000 * Math.pow(2, autoSyncAttemptRef.current - 1), 60000)

    autoSyncTimeoutRef.current = setTimeout(async () => {
      if (!navigator.onLine) return

      const size = await getQueueSize()
      if (size > 0) {
        const results = await triggerSync()
        const hasFailures = results.some(r => !r.success)
        if (hasFailures && autoSyncAttemptRef.current < 5) {
          scheduleRetryRef.current()
        }
      }
    }, backoffDelay)
  }, [triggerSync])

  // Keep the ref in sync with the latest scheduleRetry identity
  useEffect(() => {
    scheduleRetryRef.current = scheduleRetry
  }, [scheduleRetry])

  // Auto-sync on reconnect with exponential backoff
  useEffect(() => {
    const handleOnline = async () => {
      try {
        // Small delay to let connection stabilize
        await new Promise(resolve => setTimeout(resolve, 1000))

        const queueSize = await getQueueSize()
        if (queueSize > 0) {
          const results = await triggerSync()

          // If there were failures, schedule retry with exponential backoff
          const hasFailures = results.some(r => !r.success)
          if (hasFailures) {
            scheduleRetry()
          }
        }
      } catch (e) {
        console.error('[offline-sync] Error in online handler:', e)
      }
    }

    window.addEventListener('online', handleOnline)

    // Initial queue state check + mount sync deferred to next microtask to avoid cascading renders
    queueMicrotask(() => {
      void refreshQueueState()

      if (navigator.onLine) {
        getQueueSize().then(size => {
          if (size > 0) {
            void triggerSync()
          }
        }).catch(e => console.error('[offline-sync] Initial queue check failed:', e))
      }
    })

    return () => {
      window.removeEventListener('online', handleOnline)
      if (autoSyncTimeoutRef.current) {
        clearTimeout(autoSyncTimeoutRef.current)
      }
    }
  }, [triggerSync, refreshQueueState, scheduleRetry])

  // Periodic queue state refresh (every 10s when offline)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!navigator.onLine) {
        refreshQueueState()
      }
    }, 10000)

    return () => clearInterval(interval)
  }, [refreshQueueState])

  return {
    ...state,
    triggerSync,
    refreshQueueState,
  }
}
